import { useEffect, useCallback, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";

export default function RecentWork({ images, onOpenImage }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    loop: false,
    dragFree: true,
    containScroll: "trimSnaps",
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const prev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on("select", update);
    emblaApi.on("reInit", update);
    update();
  }, [emblaApi]);

  return (
    <section className="bg-white" data-testid="recent-work-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 md:mb-10">
          <div>
            <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">Recent installations</div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-slate-900 tracking-tight mt-2">
              Our latest work across Nagpur
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl">
              Real projects delivered by the Hayat team — from small retail CCTV setups to full-scale surveillance and networking roll-outs.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={prev}
              disabled={!canPrev}
              data-testid="recent-work-prev"
              aria-label="Previous"
              className="w-11 h-11 rounded-full border border-slate-200 text-slate-700 hover:border-[#1C6B8A] hover:text-[#1C6B8A] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <button
              onClick={next}
              disabled={!canNext}
              data-testid="recent-work-next"
              aria-label="Next"
              className="w-11 h-11 rounded-full bg-[#1C6B8A] text-white hover:bg-[#155670] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div ref={emblaRef} className="overflow-hidden -mx-2">
          <div className="flex">
            {images.map((img, idx) => (
              <button
                key={img.id}
                onClick={() => onOpenImage && onOpenImage(idx)}
                data-testid={`recent-work-${img.id}`}
                className="group flex-[0_0_85%] sm:flex-[0_0_48%] lg:flex-[0_0_32%] min-w-0 px-2 text-left"
              >
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-slate-200 card-soft bg-slate-900">
                  {/* Blurred backdrop */}
                  <img
                    src={img.url}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60"
                    loading="lazy"
                  />
                  <img
                    src={img.url}
                    alt={img.title}
                    className="relative w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent" />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 text-[11px] font-semibold text-[#1C6B8A]">
                    #{String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <div className="font-display font-semibold text-base leading-tight">{img.title}</div>
                    {img.location && (
                      <div className="flex items-center gap-1 text-xs text-white/80 mt-1">
                        <MapPin className="w-3 h-3" />
                        {img.location}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
